import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowRightLeft, Database, LayoutTemplate, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { toggleFavorite, useFavorites } from '@/app/site/favorites';
import { useSiteScope } from '@/app/site/useSiteScope';
import {
  CatalogCard,
  CatalogGrid,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  StarButton,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { useViewMode } from '@/features/catalog/useViewMode';
import { PAGES_SLUG, usePendingTransfer, useSchemaTypes } from '@/features/catalog/useSchemaTypes';
import { SchemaMenu } from '@/features/schemaio/SchemaMenu';
import { TemplatesDialog } from '@/features/templates/TemplatesDialog';
import { useTemplates } from '@/features/templates/useTemplates';
import { contentTypesRestore } from '@/shared/api/generated/content/content';
import type { TypeBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { pluralize } from '@/shared/lib/plural';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { toast } from '@/shared/ui/Toast';

import { RECORDS } from '../SchemaPage/kinds';

/**
 * «Модели» — данные сайта плитками: кейсы, услуги, статьи, категории.
 * Звёздочка добавляет модель в избранное — в сайдбар; клик — деталка с
 * записями, полями и настройками. Страницы и компоненты — свои разделы.
 */
export function ModelsPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/models/' });
  const scope = useSiteScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useViewMode('models');
  const [search, setSearch] = useState('');
  const [trashOpen, setTrashOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const favorites = useFavorites(siteId);
  const pending = usePendingTransfer(siteId, env);
  const shaper = scope?.can('site.shape') ?? false;
  const templates = useTemplates(siteId, env, 'content');

  const types = useSchemaTypes(siteId, env);
  const trash = useSchemaTypes(siteId, env, true);

  // Формы — свой раздел: заявки не смешиваются с данными сайта.
  const isModel = (type: TypeBody) =>
    type.kind !== 'block' && type.slug !== PAGES_SLUG && type.purpose === 'content';
  const needle = search.trim().toLowerCase();
  const models = (types.data ?? [])
    .filter(isModel)
    .filter((type) => needle === '' || `${type.name} ${type.slug}`.toLowerCase().includes(needle));
  const trashed = (trash.data ?? []).filter(isModel);

  const star = (type: TypeBody) => (
    <StarButton
      active={favorites.includes(type.slug)}
      onToggle={() => {
        toggleFavorite(siteId, type.slug);
      }}
    />
  );

  const meta = (type: TypeBody) => (
    <>
      <span>{type.kind === 'single' ? 'одна запись' : pluralize(type.records_count, RECORDS)}</span>
      {type.manual_order && <span>ручной порядок</span>}
      {type.route !== '' && <span className={catalog['mono']}>{type.route}</span>}
    </>
  );

  const restore = (type: TypeBody) => {
    contentTypesRestore(type.id)
      .then(() => {
        toast.success(`«${type.name}» вернулась`);
        void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось вернуть');
      });
  };

  const create = () => {
    void navigate({ to: '/sites/$siteId/$env/models/new', params: { siteId, env } });
  };

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title="Модели"
        subtitle="Данные сайта: кейсы, услуги, статьи, категории. Звёздочка добавляет модель в избранное — в сайдбар."
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {shaper && (
              <Button
                asChild
                variant="outline"
                tone="neutral"
                iconLeft={<ArrowRightLeft size={15} />}
              >
                <Link to="/sites/$siteId/$env/transfer" params={{ siteId, env }}>
                  Перенос
                  {pending > 0 && (
                    <Badge size="sm" tone="accent">
                      {pending}
                    </Badge>
                  )}
                </Link>
              </Button>
            )}
            {shaper && (
              <SchemaMenu siteId={siteId} env={env} siteSlug={scope?.site?.slug ?? 'site'} />
            )}
            {shaper && (templates.data?.length ?? 0) > 0 && (
              <Button
                variant="outline"
                tone="neutral"
                iconLeft={<LayoutTemplate size={15} />}
                onClick={() => {
                  setTemplatesOpen(true);
                }}
              >
                Из шаблона
              </Button>
            )}
            {shaper && (
              <Button iconLeft={<Plus size={15} />} onClick={create}>
                Новая модель
              </Button>
            )}
          </>
        }
      />

      <TemplatesDialog
        siteID={siteId}
        env={env}
        purpose="content"
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
      />

      <div className={catalog['bar']}>
        <CatalogSearch
          value={search}
          onChange={setSearch}
          placeholder="Поиск по названию или ключу"
        />
        {trashed.length > 0 && (
          <Button
            variant={trashOpen ? 'solid' : 'ghost'}
            tone="neutral"
            iconLeft={<Trash2 size={15} />}
            onClick={() => {
              setTrashOpen((value) => !value);
            }}
          >
            Корзина · {trashed.length}
          </Button>
        )}
      </div>

      {trashOpen ? (
        <CatalogTable head={['Название', 'Ключ', '']}>
          {trashed.map((type) => (
            <tr key={type.id}>
              <td>{type.name}</td>
              <td className={catalog['mono']}>{type.slug}</td>
              <td>
                {shaper && (
                  <Button
                    size="sm"
                    variant="outline"
                    tone="neutral"
                    iconLeft={<RotateCcw size={14} />}
                    onClick={() => {
                      restore(type);
                    }}
                  >
                    Вернуть
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </CatalogTable>
      ) : types.isPending ? null : models.length === 0 && needle === '' ? (
        <p className={catalog['empty']}>
          Моделей пока нет. Начните с того, что сайт публикует чаще всего: кейсов, статей, услуг.
        </p>
      ) : view === 'grid' ? (
        <CatalogGrid>
          {models.map((type) => (
            <CatalogCard
              key={type.id}
              link={{
                to: '/sites/$siteId/$env/models/$typeId',
                params: { siteId, env, typeId: type.id },
              }}
              icon={<Database size={18} />}
              title={type.name}
              subtitle={type.slug}
              meta={meta(type)}
              aside={star(type)}
            />
          ))}
          {shaper && needle === '' && (
            <NewCard onClick={create}>
              <Plus size={20} />
              Новая модель
            </NewCard>
          )}
        </CatalogGrid>
      ) : (
        <CatalogTable head={['Название', 'Ключ', 'Записей', 'Адрес записи', '']}>
          {models.map((type) => (
            <tr key={type.id}>
              <td>
                <Link
                  to="/sites/$siteId/$env/models/$typeId"
                  params={{ siteId, env, typeId: type.id }}
                >
                  {type.name}
                </Link>
              </td>
              <td className={catalog['mono']}>{type.slug}</td>
              <td className={catalog['num']}>
                {type.kind === 'single' ? '1' : type.records_count}
              </td>
              <td className={catalog['mono']}>{type.route || '—'}</td>
              <td>{star(type)}</td>
            </tr>
          ))}
        </CatalogTable>
      )}
    </div>
  );
}
